/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

import type { ExpandedEvent } from '@features/agenda/utils/expand-recurrence';
import type { Calendar } from '@services/requests/housien-models/Calendar';
import { DayView } from '@features/agenda/components/DayView';
import { WeekView } from '@features/agenda/components/WeekView';
import { MonthView } from '@features/agenda/components/MonthView';
import { weekDates, threeDayDates } from '@features/agenda/utils/agenda-dates';

export type AgendaCalendarViewProps = {
  view: string;
  anchor: Date;
  events: ExpandedEvent[];
  calendars: Calendar[];
  canCreateEvents: boolean;
  onSelect: (event: ExpandedEvent) => void;
  onSlotCreate: (slotDate?: Date) => void;
  onDayClick: (date: Date) => void;
};

/**
 * Renders the active calendar view (day / week / month) for the agenda page.
 * Extracted from {@link AgendaPage}.
 */
export const AgendaCalendarView = ({
  view,
  anchor,
  events,
  calendars,
  canCreateEvents,
  onSelect,
  onSlotCreate,
  onDayClick,
}: AgendaCalendarViewProps) => (
  <>
    {view === 'day' && (
      <DayView
        date={anchor}
        events={events}
        calendars={calendars}
        onSelect={onSelect}
        onSlotClick={canCreateEvents ? onSlotCreate : undefined}
      />
    )}
    {view === '3day' && (
      <WeekView
        days={threeDayDates(anchor)}
        events={events}
        calendars={calendars}
        onSelect={onSelect}
        onSlotClick={canCreateEvents ? onSlotCreate : undefined}
      />
    )}
    {view === 'week' && (
      <WeekView
        days={weekDates(anchor)}
        events={events}
        calendars={calendars}
        onSelect={onSelect}
        onSlotClick={canCreateEvents ? onSlotCreate : undefined}
      />
    )}
    {view === 'month' && (
      <MonthView
        year={anchor.getFullYear()}
        month={anchor.getMonth()}
        events={events}
        calendars={calendars}
        onDayClick={onDayClick}
        onSelect={onSelect}
      />
    )}
  </>
);
